import { useState, useEffect } from "react";
import { InboundHeaderCard } from "ui-components/Layout";
import { Success, Error_, LoadingDialog } from "ui-components/Alert";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import styled from "@emotion/styled";
import { StripedDataGrid } from "ui-components/data-grids/StripedCheckboxDataGrid";
import MDBox from "components/MDBox";
import { FormTitle } from "ui-components/Form";
import { getBillingInvoice } from "services/data.billing.service";
import { headerRender, headerValues, headerValidation } from "./components/header-schema";
import { useForm } from "react-hook-form";
import InvoiceHeader from "./components/InvoiceHeader";
import { editBillingInvoiceHeader } from "services/data.billing.service";
import { yupResolver } from "@hookform/resolvers/yup";
import MDButton from "components/MDButton";
import { GridFooter, GridFooterContainer } from "@mui/x-data-grid";

const StyledDataGrid2 = styled(StripedDataGrid)({
  "& .subtotal-row": {
    backgroundColor: "#fff9c4", // light yellow
    fontWeight: "bold",
  },
  "& .grand-total-row": {
    backgroundColor: "#ffe082", // deeper yellow
    fontWeight: "bold",
  },
  ".right-align-header": {
    textAlign: "right",
  },
});

function BillingInvoiceHeader({ invoiceNumber, viewPricePermission }) {
  const [alert, setAlert] = useState(null);

  const onError = (data) => setAlert(<Error_ data={data} onChange={setAlert} />);

  const [details, setDetails] = useState([]);

  const [formValues, setFormValues] = useState(headerValues);
  const queryClient = useQueryClient();
  const editInvoiceHeaderMutation = useMutation(editBillingInvoiceHeader, {});

  const invoiceDetailColumns = [
    {
      field: "descriptions",
      headerName: "Descriptions",
      sortable: false,
      align: "left",
      flex: 1.9,
      minWidth: 190,
    },
    {
      field: "amount",
      headerName: "Amount",
      sortable: false,
      align: "right",
      flex: 1.5,
      minWidth: 150,
      headerClassName: "right-align-header",
      valueFormatter: (params) => {
        if (!viewPricePermission)
          return "**";
        if (params === null || params === undefined) {
          return "";
        }
        return params.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      },
    },
    {
      field: "discountPerc",
      headerName: "Discount Percentage",
      sortable: false,
      align: "right",
      flex: 1.5,
      minWidth: 150,
      headerClassName: "right-align-header",
      valueFormatter: (params) => {
        if (!viewPricePermission)
          return "**";
      }
    },
    {
      field: "discountAmount",
      headerName: "Discount Amount",
      sortable: false,
      align: "right",
      flex: 1.5,
      minWidth: 150,
      headerClassName: "right-align-header",
      valueFormatter: (params) => {
        if (!viewPricePermission)
          return "**";
        if (params === null || params === undefined) {
          return "";
        }
        return params.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      },
    },
    {
      field: "netAmount",
      headerName: "Net Amount",
      sortable: false,
      align: "right",
      flex: 1.5,
      minWidth: 150,
      headerClassName: "right-align-header",
      valueFormatter: (params) => {
        if (!viewPricePermission)
          return "**";
        if (params === null || params === undefined) {
          return "";
        }
        return params.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      },
    },
    {
      field: "taxAmount",
      headerName: "Tax Amount",
      sortable: false,
      align: "right",
      flex: 1.5,
      minWidth: 150,
      headerClassName: "right-align-header",
      valueFormatter: (params) => {
        if (!viewPricePermission)
          return "**";
        if (params === null || params === undefined) {
          return "";
        }
        return params.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      },
    },
  ];

  //useQuery read data
  const { isFetching } = useQuery({
    queryKey: ["billing-invoice", { invoiceNumber }],
    queryFn: () => getBillingInvoice({ invoiceNumber }),
    onSuccess: (data) => {
      const result = data.data.data;
      console.log(result);
      const { details, ...values } = result;
      setFormValues(values);
      setDetails(
        details.map((x) => {
          return { id: x.descriptions, ...x };
        })
      );
    },
    onError,
  });

  const form = useForm({
    defaultValues: formValues,
    values: formValues,
    resolver: yupResolver(headerValidation),
  });
  const {
    control,
    watch,
    formState: { errors },
    setValue,
    getValues,
    trigger,
  } = form;
  const headerProps = { headerRender, control, errors };

  const onSubmit = async () => {
    window.scrollTo(0, 0);
    const isValid = await trigger();
    if (!isValid) {
      window.scrollTo(0, 0);
      return;
    }

    const data = getValues();

    const req = {
      ...data,
    };

    editInvoiceHeaderMutation.mutate(
      { invoiceNumber, req },
      {
        onSuccess: (data) => {
          setAlert(<Success data={data} onChange={setAlert} />);
          queryClient.invalidateQueries("billing-invoice")
        },
        onError,
      }
    );
  };

  return (
    <>
      <InboundHeaderCard lg={12}>
        {isFetching && <LoadingDialog title="Fetching Billing Invoice" />}

        {alert}

        <form noValidate>
          <InvoiceHeader {...headerProps} />
        </form>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          <MDButton color="info" variant="gradient" onClick={onSubmit}>
            Save
          </MDButton>
        </div>

        {Boolean(details) && (
          <MDBox mt={1.625}>
            <FormTitle title={"Billing Invoice Details"} />
            <StyledDataGrid2
              columns={invoiceDetailColumns}
              // rows={details.map((x) => {
              //   return { id: x.descriptions, ...x };
              // })}
              rows={[
                ...details.map((x) => ({
                  id: x.descriptions, // ensure unique value
                  ...x,
                })),
                {
                  id: "subtotal-row",
                  descriptions: "Sub-total",
                  netAmount: details.reduce((sum, obj) => sum + obj.netAmount, 0),
                  taxAmount: details.reduce((sum, obj) => sum + (obj.taxAmount ?? 0), 0),
                },
                {
                  id: "grand-total-row",
                  descriptions: "Grand Total",
                  taxAmount: details.reduce(
                    (sum, obj) => sum + obj.netAmount + (obj.taxAmount ?? 0),
                    0
                  ),
                },
              ]}
              slots={{
                footer: () => {return (
                  <GridFooterContainer>
                    {/* <div>
                      <span style={{ marginLeft: "1rem", fontWeight: "bold" }}>
                        Total Amount:{" "}
                        {details
                          .reduce((sum, obj) => sum + obj.netAmount, 0)
                          .toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                      </span>
                    </div>
                   */}
                    <GridFooter
                      sx={{
                        border: "none",
                      }}
                    />
                  </GridFooterContainer>
                )}
              }}
            />
          </MDBox>
        )}
      </InboundHeaderCard>
    </>
  );
}

export default BillingInvoiceHeader;
